import {
  MONTH_NAMES,
  QUARTER_HOUR_ISSUE,
  daysInMonthForYearly,
  duplicateTime,
  formatTime12h,
  friendlyToCronExpression,
  intervalFromCron,
  isQuarterHourMinute,
  MAX_RUN_DATE_DAYS,
  isRealDate,
  localDateOf,
  nextFireAt,
  nextFireAtExcluding,
  offQuarterMinutes,
  startOfLocalDate,
  validateCron,
  yearDateFromCron,
  yearDateIssue,
  type FriendlySchedule,
  type IntervalMinutes,
} from "@shared/cron/cronUtils";
import { byCodeUnit } from "@shared/sort";
import type { ConnectorSummary } from "@shared/types/connectors";
import type { DataflowSummary } from "@shared/types/dataflows";
import type { FriendlyDatesBody, FriendlyScheduleBody } from "@shared/types/schedules";
import {
  isStoredRunDate,
  resolveOnce,
  runDateGapIssue,
  runDateRowInstant,
} from "@/lib/schedules/once";
import {
  formatNextFire,
  offeredZoneFor,
  timezoneAbbreviation,
  timezoneShortLabel,
} from "@/lib/timezones";
import { zoneAbbreviation } from "@/lib/time/displayTime";

/**
 * The schedule editor's form state and the one place it becomes a cron
 * expression (FR-50).
 *
 * The friendly builder is the source of truth and the cron string is only ever
 * shown back, never typed. That is a deliberate narrowing: `schedules.cron`
 * holds one five-field expression, the server accepts either source, and a
 * screen that offered both would let a user write an expression the builder
 * cannot round-trip and then silently lose their edit the next time they opened
 * it.
 *
 * The collapse to one expression is `friendlyToCronExpression` in
 * `shared/cron/cronUtils.ts` — the SAME function the server uses, imported
 * rather than reimplemented, so "the friendly form round-trips to the same
 * cron" is true by construction instead of by a test that has to keep two
 * copies in step.
 */

/** The builder's frequency: the friendly body's, and Once's `Dates` (round 10, C1). */
export type ScheduleFrequency = FriendlyScheduleBody["frequency"] | FriendlyDatesBody["frequency"];

/**
 * What the segmented Frequency control offers (round 10, C1, in the style of Claude Routines). UI
 * only: each maps onto a builder frequency. Hourly is an Interval of 60 minutes over the whole
 * day, Weekdays is Weekly on Monday to Friday, and Custom is the Interval builder (and where a
 * stored expression the builder cannot represent is kept).
 */
export const SCHEDULE_SEGMENTS = [
  "Once",
  "Hourly",
  "Daily",
  "Weekdays",
  "Weekly",
  "Monthly",
  "Yearly",
  "Custom",
] as const;
export type ScheduleSegment = (typeof SCHEDULE_SEGMENTS)[number];

/**
 * Once (round 10, C1) was shown and not offered until the server and the runner ran it; round 10
 * wave 2 (S3) built them, so it is on. Off again, the segment is disabled with `ONCE_COMING_SOON`
 * and a draft in it is never saved.
 */
export const ONCE_AVAILABLE = true;

export const ONCE_COMING_SOON = "Coming soon";

/** Why a Once draft is not saved while `ONCE_AVAILABLE` is false. */
export const ONCE_UNAVAILABLE_REASON =
  "Running on chosen dates is coming soon. Choose another frequency to save.";

/** Monday to Friday, as the Weekdays segment writes them (0 = Sunday). */
export const WEEKDAY_VALUES: readonly number[] = [1, 2, 3, 4, 5];

/** The Hourly segment's body: every 60 minutes from midnight to midnight, every day. */
const HOURLY_FRIENDLY: FriendlySchedule = {
  frequency: "Interval",
  intervalMinutes: 60,
  windowStart: "12:00AM",
  windowEnd: "12:00AM",
  weekDays: [],
};

/** Yearly: the month and day are both chosen before the builder makes an expression. */
/** The summary line while Yearly has no month and day, and while Monthly has no day (W2-03). */
export const YEARLY_HINT = "Choose a month and day";
export const MONTHLY_DAYS_HINT = "Choose at least one day";

export const YEAR_DATE_REASON = "Choose the month and day this schedule runs.";

/**
 * The platform's rule for a firing that meets its previous run still going (one run per
 * connection at a time; 2026-09-28). Stated under "This schedule runs" for every frequency.
 */
export const SKIP_RULE_SENTENCE =
  "If the previous run is still running at a firing, that firing is skipped and the next one runs.";

/** On a time row with no time, when another row exists (2026-09-28). */
export const INCOMPLETE_TIME_ISSUE = "Choose a time, or remove this row.";

/** Beside Save while a time row has no time. */
export const INCOMPLETE_TIMES_REASON = "Choose a time for each row, or remove the empty one.";

/** While the interval has no step or no window yet. */
export const INCOMPLETE_INTERVAL_REASON =
  "Choose how often it runs, and the hours it runs between.";

/** 0 = Sunday, matching `friendlyScheduleSchema`. Listed Monday first, which is
 * how a person reads a working week; the VALUES stay the schema's. */
export const WEEK_DAYS: ReadonlyArray<{ value: number; label: string; short: string }> = [
  { value: 1, label: "Monday", short: "Mon" },
  { value: 2, label: "Tuesday", short: "Tue" },
  { value: 3, label: "Wednesday", short: "Wed" },
  { value: 4, label: "Thursday", short: "Thu" },
  { value: 5, label: "Friday", short: "Fri" },
  { value: 6, label: "Saturday", short: "Sat" },
  { value: 0, label: "Sunday", short: "Sun" },
];

export const MONTH_DAYS: readonly number[] = Array.from({ length: 31 }, (_, i) => i + 1);

/**
 * The picker's minute step (round 5, owner 2026-10-01: "yes, go with 15-minute steps"): its minute
 * column offers :00, :15, :30 and :45, the quarter hours the server accepts for a new or changed
 * time (`QUARTER_HOUR_MINUTES`).
 */
export const QUARTER_HOUR_STEP = 15;

/**
 * The minute of the first time off the quarter hour, or null. Only a stored schedule can hold one
 * (the picker cannot write it), and its times share one minute (`friendlyToCronExpression`).
 */
export function offQuarterMinuteOf(
  draft: Pick<ScheduleDraft, "frequency" | "times">,
): number | null {
  if (draft.frequency === "Interval" || draft.frequency === "Dates") return null;
  for (const time of draft.times) {
    const match = time === null ? null : /^\d{1,2}:(\d{2})$/.exec(time.trim());
    if (!match) continue;
    const minute = Number.parseInt(match[1] as string, 10);
    if (!isQuarterHourMinute(minute)) return minute;
  }
  return null;
}

/** Under the times of a schedule stored off the quarter hour (round 5). */
export function offQuarterNote(minute: number): string {
  return `This schedule runs at :${String(minute).padStart(2, "0")}. New times use quarter hours. Pick one to change it.`;
}

/** `13:05` → `01:05PM`, the form `friendlyScheduleSchema` accepts. */
export function toTime12h(hour24: number, minute: number): string {
  const period = hour24 >= 12 ? "PM" : "AM";
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return `${String(hour12).padStart(2, "0")}:${String(minute).padStart(2, "0")}${period}`;
}

/** `13:05` → `01:05PM`; throws on anything that is not `HH:MM`. */
export function fromTime24h(time24h: string): string {
  const match = /^(\d{1,2}):(\d{2})$/.exec(time24h.trim());
  if (!match) throw new Error(`Invalid 24-hour time: ${JSON.stringify(time24h)}`);
  const hour = Number.parseInt(match[1] as string, 10);
  const minute = Number.parseInt(match[2] as string, 10);
  if (hour > 23 || minute > 59) throw new Error(`Invalid 24-hour time: ${JSON.stringify(time24h)}`);
  return toTime12h(hour, minute);
}

export interface ScheduleDraft {
  name: string;
  timezone: string;
  enabled: boolean;
  /**
   * `null` until the user chooses one (Session 10, B4): a new schedule starts with nothing
   * chosen, so nothing can be saved that the user did not pick.
   */
  frequency: ScheduleFrequency | null;
  /**
   * One entry per time row: a 24-hour `HH:MM`, or `null` for a row with no time chosen yet
   * ("Add another time", or a picker cleared). Empty until one is chosen: the editor then shows
   * one empty row that is not in the draft. A `null` row is never dropped on Save; it blocks it
   * (2026-09-28, owner: "selected only the hour ... it just saved and ignored").
   */
  times: Array<string | null>;
  /**
   * The segment chosen on the Frequency control (round 10, C1); `null` with no frequency. Two
   * segments share a frequency (Hourly and Custom are Interval, Weekdays and Weekly are Weekly),
   * so the draft keeps which one the person chose: ticking Friday on Weekly does not turn it
   * into Weekdays under their pointer.
   */
  segment: ScheduleSegment | null;
  /** Monday first in the UI; 0 = Sunday. Weekly and Interval. */
  weekDays: number[];
  monthDays: number[];
  /** Yearly only: the month (1..12) and day; each `null` until chosen. */
  yearDate: { month: number | null; day: number | null };
  /**
   * Once only (round 10, C1): one row per run, a local `YYYY-MM-DD` and a 24-hour `HH:MM` in the
   * schedule's zone, each `null` until chosen. Empty shows one empty row, as `times` does.
   */
  runDates: Array<{ date: string | null; time: string | null }>;
  /** Interval only: every 15, 30 or 60 minutes; `null` until chosen. */
  intervalMinutes: IntervalMinutes | null;
  /** Interval only: the first hour of the window, 0..23 (a run); `null` until chosen. */
  windowStart: number | null;
  /** Interval only: the hour the window ends BEFORE, 1..24 (24 is midnight); `null` until chosen. */
  windowEnd: number | null;
  /**
   * The connections this schedule imports. Stored in the order chosen, but the
   * runner extracts them in parallel (up to the instance and per-connector
   * limits), so nothing treats it as a run order and the editor shows none.
   */
  dataflowIds: string[];
  dbtJobIds: string[];
  /**
   * The stored expression, when the builder cannot represent it (SF2): a schedule written
   * through the API as `*\/5 * * * *` has no Daily/Weekly/Monthly form. It is kept, shown
   * read-only as "Custom schedule: …" and sent back as `cron`, until the user completes the
   * builder with a frequency and a time; then the builder wins.
   */
  customCron: string | null;
  /**
   * Local days (`YYYY-MM-DD`, in `timezone`) on which the schedule does not fire (Session 10,
   * P9). Sent as they are; the server drops days already past and duplicates.
   */
  skipDates: string[];
  /**
   * Round 4, item 7: the connections of this schedule that import every row again on each
   * scheduled run; the others use their own refresh type. Chosen on each incremental connection's
   * row, in the schedule's order. Replaces the schedule-wide `refreshMode`, which the server now
   * derives.
   */
  fullRefreshDataflowIds: string[];
  /**
   * Round 7: "Skip when a source is stale". Offered only for a schedule of dbt jobs and no
   * connection; a save sends it on only then (`ScheduleDbtJobsSection`).
   */
  checkSourceFreshness: boolean;
  /**
   * The saved row's pause, read-only here (Pause and Resume are on the Schedules list). Carried in
   * the draft so the next run shown beside Save is the one the runner will keep. Never sent.
   */
  pausedUntil: string | null;
  /**
   * Round 5: the expression the saved row holds, read-only (null for a new schedule). A schedule
   * stored off the quarter hour saves unchanged while the draft still resolves to exactly this
   * expression; any other expression must be on the quarter hour. Never sent.
   */
  storedCron?: string | null;
  /**
   * Round 10 wave 2 (S3): a saved Once schedule's instants (ISO), read-only. A row the schedule
   * already holds saves as it is, past or not (the server's rule), so it is never "already past".
   */
  storedRunDates?: readonly string[];
}

/**
 * Round 6, S2: whether a connection offers Incremental or Full on a schedule row. The CONNECTOR
 * decides (`isIncrementalSupported`, from `GET /connectors`), not the connection's saved refresh
 * type: the trigger chooses the mode now. While the catalog is loading, or for a connector it does
 * not list, the connection's own refresh type stands in, which is what the instance migration
 * `refresh_mode_on_schedule` made it agree with.
 */
export function incrementalCapable(
  connection: Pick<DataflowSummary, "connectorName" | "refreshType">,
  connectors: ReadonlyMap<string, Pick<ConnectorSummary, "isIncrementalSupported">>,
): boolean {
  const connector = connectors.get(connection.connectorName);
  return connector ? connector.isIncrementalSupported : connection.refreshType === "incremental";
}

/**
 * Round 4, item 7: the set a save sends. In the schedule's order, only its own connections, and
 * less a connection this screen knows cannot import incrementally (round 6, S2: by its connector):
 * such a row shows no choice, so a stored id there would force it again, unseen. A connection the
 * screen cannot see (still loading, or gone) is kept as stored.
 */
export function fullRefreshToSave(
  draft: Pick<ScheduleDraft, "dataflowIds" | "fullRefreshDataflowIds">,
  connections: readonly Pick<DataflowSummary, "id" | "connectorName" | "refreshType">[],
  connectors: readonly Pick<ConnectorSummary, "name" | "isIncrementalSupported">[] = [],
): string[] {
  const chosen = new Set(draft.fullRefreshDataflowIds);
  const catalog = new Map(connectors.map((connector) => [connector.name, connector]));
  const byId = new Map(connections.map((row) => [row.id, row]));
  return draft.dataflowIds.filter((id) => {
    if (!chosen.has(id)) return false;
    const connection = byId.get(id);
    return connection ? incrementalCapable(connection, catalog) : true;
  });
}

/**
 * The zone a NEW schedule starts in (owner rule on #1078, 2026-10-15: the person's zone, else
 * Pacific, never UTC).
 *
 * The person's display zone (`useDisplayTz()`: their preference, else this browser's) when the
 * picker offers it, aliased onto the offered list (Detroit is Eastern, Boise is Mountain). Anything
 * else, a person in another country or a display zone of UTC, starts in Pacific: Skypoint is in
 * Portland. The picker still offers UTC, and a person who picks it on purpose keeps it: that
 * schedule's own rule then reads in UTC, while its next run reads in their display zone (#1079).
 * With no display zone given, the browser's zone stands in for it.
 */
export const FALLBACK_TIMEZONE = "America/Los_Angeles";

function offeredDefault(zone: string | null | undefined): string | null {
  const offered = offeredZoneFor(zone);
  return offered && offered !== "UTC" ? offered : null;
}

export function defaultTimezone(displayZone?: string | null): string {
  if (displayZone) return offeredDefault(displayZone) ?? FALLBACK_TIMEZONE;
  try {
    return offeredDefault(Intl.DateTimeFormat().resolvedOptions().timeZone) ?? FALLBACK_TIMEZONE;
  } catch {
    return FALLBACK_TIMEZONE;
  }
}

/**
 * A new schedule: the zone is pre-filled (it is a zone, not a time, and it is shown beside
 * Save), and the frequency and time are NOT (Session 10, B4 and SF1). The owner's complaint was
 * a schedule saved at a 02:00 nobody chose; there is no default time to save any more.
 */
export function emptyDraft(displayZone?: string | null): ScheduleDraft {
  return {
    name: "",
    timezone: defaultTimezone(displayZone),
    enabled: true,
    frequency: null,
    segment: null,
    times: [],
    weekDays: [],
    monthDays: [],
    yearDate: { month: null, day: null },
    runDates: [],
    intervalMinutes: null,
    windowStart: null,
    windowEnd: null,
    dataflowIds: [],
    dbtJobIds: [],
    customCron: null,
    skipDates: [],
    fullRefreshDataflowIds: [],
    checkSourceFreshness: false,
    pausedUntil: null,
    storedCron: null,
  };
}

/**
 * True when the user has said when the schedule runs: a frequency and at least one time, or,
 * for an interval, its step and both window edges. A row with no time next to a chosen one is
 * still a chosen time; that row blocks Save on its own (`timeRowIssues`).
 */
export function hasChosenTime(draft: ScheduleDraft): boolean {
  if (draft.frequency === null) return false;
  if (draft.frequency === "Interval") {
    return draft.intervalMinutes !== null && draft.windowStart !== null && draft.windowEnd !== null;
  }
  if (draft.frequency === "Dates") return draft.runDates.some((row) => row.time !== null);
  return draft.times.some((time) => time !== null);
}

/**
 * The segment the Frequency control shows: the one chosen, else Custom while a stored expression
 * the builder cannot represent is kept (SF2), else none.
 */
export function shownSegment(draft: ScheduleDraft): ScheduleSegment | null {
  if (draft.segment) return draft.segment;
  // A draft built with a frequency and no segment (an older caller) reads by its frequency.
  switch (draft.frequency) {
    case "Dates":
      return "Once";
    case "Interval":
      return "Custom";
    case "Daily":
    case "Weekly":
    case "Monthly":
    case "Yearly":
      return draft.frequency;
    default:
      return draft.customCron && !hasChosenTime(draft) ? "Custom" : null;
  }
}

/**
 * The draft once a segment is chosen (round 10, C1). Each writes its frequency and the fields that
 * frequency fixes; a list the new frequency does not use is cleared, so a stale weekDays list is
 * never sent on a Daily schedule and read back as a change that never happened.
 */
export function applySegment(
  draft: ScheduleDraft,
  segment: ScheduleSegment,
  now: Date = new Date(),
): ScheduleDraft {
  const keepsDays = draft.frequency === "Weekly" || draft.frequency === "Interval";
  const base = { ...draft, segment, monthDays: [] as number[] };
  switch (segment) {
    case "Once":
      return { ...base, frequency: "Dates", weekDays: [] };
    case "Hourly":
      return {
        ...base,
        frequency: "Interval",
        intervalMinutes: 60,
        windowStart: 0,
        windowEnd: 24,
        weekDays: [],
      };
    case "Daily":
      return { ...base, frequency: "Daily", weekDays: [] };
    case "Weekdays":
      return { ...base, frequency: "Weekly", weekDays: [...WEEKDAY_VALUES] };
    case "Weekly":
      // Weekly opens on ONE day, today's in the schedule's zone; Weekdays is the Monday to Friday
      // option, so coming from it (or from no days) does not carry five days over (W2-03).
      return {
        ...base,
        frequency: "Weekly",
        weekDays:
          keepsDays && draft.segment !== "Weekdays" && draft.weekDays.length > 0
            ? draft.weekDays
            : [weekdayOfToday(draft.timezone, now)],
      };
    case "Monthly":
      return { ...base, frequency: "Monthly", weekDays: [], monthDays: draft.monthDays };
    case "Yearly":
      return { ...base, frequency: "Yearly", weekDays: [] };
    case "Custom":
      return { ...base, frequency: "Interval", weekDays: keepsDays ? draft.weekDays : [] };
  }
}

/** Today's weekday in `zone`, 0 = Sunday (the number the Weekly day list stores). */
function weekdayOfToday(zone: string, now: Date): number {
  const [year, month, day] = todayInZone(zone, now).split("-").map(Number) as [
    number,
    number,
    number,
  ];
  return new Date(Date.UTC(year, month - 1, day)).getUTCDay();
}

/** Yearly's Day list: 1 to the days `month` has every year (February to 28); 31 with no month. */
export function yearDayOptions(month: number | null): number[] {
  const count = month === null ? 31 : daysInMonthForYearly(month);
  return Array.from({ length: count }, (_, i) => i + 1);
}

/** Yearly: a new month keeps the day when that month has it, else clears it. */
export function withYearMonth(draft: ScheduleDraft, month: number): ScheduleDraft {
  const day = draft.yearDate.day;
  const keeps = day !== null && day <= daysInMonthForYearly(month);
  return { ...draft, yearDate: { month, day: keeps ? day : null } };
}

export const INCOMPLETE_RUN_DATE_ISSUE = "Choose a date and a time, or remove this row.";

export const RUN_DATE_PAST_ISSUE = "Choose a date that is not already past.";

/** A row earlier today: the day is not past, the instant is (the server's words). */
export const RUN_TIME_PAST_ISSUE = "Choose a date and time that is not already past.";

/** A new row further ahead than the server takes (`MAX_RUN_DATE_DAYS`, its words). */
export const RUN_DATE_TOO_FAR_ISSUE = `Choose a date within ${MAX_RUN_DATE_DAYS} days.`;

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * What each Once row says about itself (round 10, C1), as `timeRowIssues` for times: a row half
 * chosen next to another, a date that is not a real day, a new instant already past or more than
 * `MAX_RUN_DATE_DAYS` ahead (compared as the instant in the schedule's zone, as
 * `server/services/scheduleDates.ts` does), or the same date and time twice.
 */
export function runDateRowIssues(
  draft: ScheduleDraft,
  now: Date = new Date(),
): Array<string | null> {
  const seen = new Set<string>();
  const today = todayInZone(draft.timezone, now);
  return draft.runDates.map((row) => {
    if (row.date !== null && !isRealDate(row.date)) return "Use a real date such as 2026-12-25.";
    // A day already past in the schedule's zone would never run; one the saved schedule holds
    // (it has run) saves as it is (round 10 wave 2, S3).
    const kept =
      row.date !== null &&
      row.time !== null &&
      isStoredRunDate(row.date, row.time, draft.timezone, draft.storedRunDates);
    if (row.date !== null && row.date < today && !kept) return RUN_DATE_PAST_ISSUE;
    if (row.date === null || row.time === null) {
      return draft.runDates.length > 1 || row.date !== null || row.time !== null
        ? INCOMPLETE_RUN_DATE_ISSUE
        : null;
    }
    const key = `${row.date} ${row.time.trim()}`;
    if (seen.has(key)) return "This date and time is already in this schedule.";
    // Round 10 wave 2 (S3): a time the spring change skips, in the server's words.
    const gap = runDateGapIssue(row.date, row.time, draft.timezone);
    if (gap) return gap;
    // Earlier today, or past the server's horizon: refused there, so refused here first.
    const at = kept ? null : runDateRowInstant(row.date, row.time, draft.timezone);
    if (at !== null && at.getTime() <= now.getTime()) return RUN_TIME_PAST_ISSUE;
    if (at !== null && at.getTime() > now.getTime() + MAX_RUN_DATE_DAYS * DAY_MS) {
      return RUN_DATE_TOO_FAR_ISSUE;
    }
    seen.add(key);
    return null;
  });
}

/** The draft's chosen times, the rows with no time left out. */
function chosenTimes(draft: ScheduleDraft): string[] {
  return draft.times.filter((time): time is string => time !== null);
}

/**
 * What each time row says about itself, by row (2026-09-28): a row with no time next to another
 * row ("Choose a time, or remove this row."), or the second occurrence of a time ("2:30 AM is
 * already in this schedule."); `null` for a row with nothing to say. An interval uses no rows.
 */
export function timeRowIssues(draft: ScheduleDraft): Array<string | null> {
  if (draft.frequency === "Interval" || draft.frequency === "Dates") {
    return draft.times.map(() => null);
  }
  const issues: Array<string | null> = draft.times.map((time) =>
    time === null && draft.times.length > 1 ? INCOMPLETE_TIME_ISSUE : null,
  );
  // Every repeat after its first, not only the first one.
  const seen = new Set<string>();
  draft.times.forEach((time, index) => {
    if (time === null) return;
    const key = time.trim();
    if (seen.has(key)) issues[index] = duplicateIssue(key);
    seen.add(key);
  });
  return issues;
}

/** "2:30 AM is already in this schedule.", in the shared rule's words (the server's too). */
function duplicateIssue(time24h: string): string {
  try {
    const slot = fromTime24h(time24h);
    return duplicateTime([slot, slot])?.issue ?? `${time24h} is already in this schedule.`;
  } catch {
    return `${time24h} is already in this schedule.`;
  }
}

/** An hour of the day (0..24) as the stored 12-hour whole hour, `10:00AM`; 24 is `12:00AM`. */
function hourTo12h(hour: number): string {
  return toTime12h(hour % 24, 0);
}

/**
 * An enabled schedule with no allowed firing inside two years (its skip dates cover every one, or
 * its cron has none): Session 10, P9. The list reads exactly this; the editor adds how to fix it.
 */
export const NO_UPCOMING_RUN = "No upcoming run";

/**
 * A disabled schedule has no next run (#1237): the editor's "This schedule runs" box and the
 * summary under Frequency say so in the words the Schedules list's Next run cell uses.
 */
export const NOT_SCHEDULED_WHILE_DISABLED = "Not scheduled while disabled";
export const NO_UPCOMING_RUN_EDITOR = "No upcoming run. Edit the schedule to recompute.";

/** The sentence Save shows while no time has been chosen (Session 10, B4). */
export const CHOOSE_TIME_REASON = "Choose when this schedule runs.";

/**
 * The draft as the friendly body. Once answers the `Dates` shape (round 10, C1;
 * `datesScheduleSchema` on the server), its rows in the stored 12-hour form.
 */
export function toFriendly(draft: ScheduleDraft): FriendlySchedule | FriendlyDatesBody {
  if (draft.frequency === "Dates") {
    return {
      frequency: "Dates",
      runDates: draft.runDates.flatMap((row) =>
        row.date !== null && row.time !== null
          ? [{ date: row.date, time: fromTime24h(row.time) }]
          : [],
      ),
    };
  }
  return toBuilderFriendly(draft);
}

/** The draft as the cron builder's body: every frequency but Once. */
function toBuilderFriendly(draft: ScheduleDraft): FriendlySchedule {
  if (draft.frequency === "Dates") throw new Error(ONCE_UNAVAILABLE_REASON);
  if (draft.frequency === null) {
    // Unreachable from the editor, which never saves without a frequency; a throw rather than a
    // silent "Daily", because a silent default is exactly what this field stopped having.
    throw new Error("A schedule needs a frequency before it becomes a cron expression.");
  }
  if (draft.frequency === "Interval" && draft.segment === "Hourly") {
    // Hourly shows no fields, so nothing hidden is sent: every hour of every day.
    return HOURLY_FRIENDLY;
  }
  if (draft.frequency === "Interval") {
    if (draft.intervalMinutes === null || draft.windowStart === null || draft.windowEnd === null) {
      throw new Error(INCOMPLETE_INTERVAL_REASON);
    }
    return {
      frequency: "Interval",
      intervalMinutes: draft.intervalMinutes,
      windowStart: hourTo12h(draft.windowStart),
      windowEnd: hourTo12h(draft.windowEnd),
      weekDays: draft.weekDays,
    };
  }
  const times = {
    cronSchedules: chosenTimes(draft).map(fromTime24h),
    weekDays: draft.frequency === "Weekly" ? draft.weekDays : [],
    monthDays: draft.frequency === "Monthly" ? draft.monthDays : [],
  };
  if (draft.frequency === "Yearly") {
    const { month, day } = draft.yearDate;
    if (month === null || day === null) throw new Error(YEAR_DATE_REASON);
    return { frequency: "Yearly", ...times, yearDate: { month, day } };
  }
  return { frequency: draft.frequency, ...times };
}

export type ResolvedSchedule =
  | {
      ok: true;
      /** Null for Once (round 10 wave 2, S3): its dates are `runDates`, with no expression. */
      cron: string | null;
      description: string;
      nextFireAt: Date | null;
      /**
       * `custom`: the stored expression the builder cannot represent, kept as it is (SF2).
       * `dates`: a Once draft (round 10 wave 2, S3).
       */
      source: "builder" | "custom" | "dates";
      /** Once only: the instants (ISO), ascending. */
      runDates?: string[];
    }
  | { ok: false; issue: string };

/**
 * The next run the runner will keep: past the draft's skip dates and the row's pause, by day
 * (Session 10, P9), with the same shared helper the server and the due scan use. Null is "No
 * upcoming run" inside two years.
 */
function nextAllowedRun(cron: string, draft: ScheduleDraft, now: Date): Date | null {
  const pausedUntil = draft.pausedUntil ? new Date(draft.pausedUntil) : null;
  return nextFireAtExcluding(cron, draft.timezone, now, draft.skipDates, pausedUntil);
}

/**
 * The expression the draft resolves to, with the sentence a person can check
 * it against.
 *
 * Every failure mode the builder can reach answers with a sentence naming the
 * fix, because this text goes into a live region under the time fields and
 * "invalid" tells the reader nothing they can act on. The two real ones are a
 * malformed time and times that do not share a minute past the hour — the
 * latter cannot be expressed in one five-field expression at all, which is the
 * server's rule, stated here before the save rather than after it.
 */
export function resolveSchedule(draft: ScheduleDraft, now: Date = new Date()): ResolvedSchedule {
  // Round 10 wave 2 (S3): Once has dates, not an expression (`client/src/lib/schedules/once.ts`).
  if (draft.frequency === "Dates") return resolveOnce(draft, runDateRowIssues(draft, now), now);
  if (!hasChosenTime(draft) && draft.customCron) {
    // The stored expression stands until the builder is complete (SF2).
    return resolveCustomCron(draft, draft.customCron, now);
  }
  const issue = builderIssue(draft, now);
  if (issue !== null) return { ok: false, issue };
  let cron: string;
  try {
    cron = friendlyToCronExpression(toBuilderFriendly(draft));
  } catch (error) {
    return { ok: false, issue: error instanceof Error ? error.message : String(error) };
  }
  const validation = validateCron(cron, draft.timezone, now);
  if (!validation.valid) return { ok: false, issue: validation.issue };
  // Round 5: the server's rule, before Save. A new or changed expression is on the quarter hour;
  // the stored one saves as it is, whatever its minute (`ScheduleService.update`).
  if (
    validation.cron !== draft.storedCron?.trim() &&
    offQuarterMinutes(validation.cron).length > 0
  ) {
    return { ok: false, issue: QUARTER_HOUR_ISSUE };
  }
  return {
    ok: true,
    cron: validation.cron,
    description: validation.description,
    nextFireAt: nextAllowedRun(validation.cron, draft, now),
    source: "builder",
  };
}

/** The stored expression the builder cannot represent, resolved as it is (SF2). */
function resolveCustomCron(draft: ScheduleDraft, customCron: string, now: Date): ResolvedSchedule {
  const custom = validateCron(customCron, draft.timezone, now);
  if (!custom.valid) return { ok: false, issue: custom.issue };
  return {
    ok: true,
    cron: custom.cron,
    description: custom.description,
    nextFireAt: nextAllowedRun(custom.cron, draft, now),
    source: "custom",
  };
}

/** Why the builder cannot make an expression yet, or null when its fields are complete. */
function builderIssue(draft: ScheduleDraft, now: Date): string | null {
  if (draft.frequency === null) return CHOOSE_TIME_REASON;
  if (draft.frequency === "Dates") {
    // Not reached from `resolveSchedule`, which hands a Once draft to `resolveOnce` first; Once
    // has no expression, so its only issues are its rows'.
    return runDateRowIssues(draft, now).find((issue) => issue !== null) ?? null;
  }
  if (draft.frequency === "Interval") {
    // The step and the window; the time rows are the other frequencies' and are ignored here.
    return hasChosenTime(draft) ? null : INCOMPLETE_INTERVAL_REASON;
  }
  if (!hasChosenTime(draft)) return "Choose the time of day this schedule runs.";
  // A row with no time blocks the schedule; it is never dropped (2026-09-28).
  if (draft.times.some((time) => time === null)) return INCOMPLETE_TIMES_REASON;
  const malformed = malformedTimeIssue(draft);
  if (malformed !== null) return malformed;
  const duplicate = timeRowIssues(draft).find((issue) => issue !== null);
  if (duplicate) return duplicate;
  if (draft.frequency === "Monthly" && draft.monthDays.length === 0) return MONTHLY_DAYS_HINT;
  if (draft.frequency === "Yearly") {
    const { month, day } = draft.yearDate;
    if (month === null || day === null) return YEAR_DATE_REASON;
    return yearDateIssue({ month, day })?.issue ?? null;
  }
  return null;
}

/**
 * Checked before the conversion so the message names the fix rather than repeating the
 * exception: `Invalid 24-hour time: "25:00"` says what is wrong and nothing about what to type
 * instead. (The same time twice is named after this, not merged: 2026-09-28.)
 */
function malformedTimeIssue(draft: ScheduleDraft): string | null {
  const malformed = chosenTimes(draft).find(
    (time) => !/^([01]\d|2[0-3]):[0-5]\d$/.test(time.trim()),
  );
  if (malformed === undefined) return null;
  return `Use a 24 hour time, for example 02:00. The hour must be between 00 and 23${
    malformed.trim() ? `. ${malformed.trim()} is not one` : ""
  }.`;
}

/**
 * The resolved schedule in one sentence, for the polite live region (UX-5,
 * NFR-7). It names the timezone every time: "at 02:00" means nothing without
 * it, and this is the value the user is most likely to have got wrong.
 *
 * By its friendly name and abbreviation — "Arizona (MST)", not
 * `America/Phoenix` — and WITHOUT the current time in that zone, which the
 * picker shows: a polite live region whose text changed every minute would be
 * announced again every minute.
 */
export function announceSchedule(
  resolved: ResolvedSchedule,
  timezone: string,
  displayZone: string = timezone,
  enabled = true,
): string {
  if (!resolved.ok) return resolved.issue;
  // The rule above is in the schedule's zone (the sentence names it). The next run is an instant,
  // so it reads in the display zone with that zone's name (#1079). A disabled schedule names no
  // next run (#1237).
  const next = !enabled
    ? ` ${NOT_SCHEDULED_WHILE_DISABLED}.`
    : resolved.nextFireAt
      ? ` Next run ${formatNextFire(resolved.nextFireAt, displayZone, instantZoneName(displayZone, resolved.nextFireAt))}.`
      : ` ${NO_UPCOMING_RUN}.`;
  const head = resolved.source === "custom" ? "Custom schedule: " : "";
  return `${head}${resolved.description}, ${timezoneShortLabel(timezone)}.${next}`;
}

export type DayPeriod = "AM" | "PM";

/** The pieces the time controls show: a 12-hour clock, as the US audience reads one. */
export interface TimeParts {
  /** "1" … "12". */
  hour: string;
  /** "00" … "59". */
  minute: string;
  period: DayPeriod;
}

/** What `SkyTimePicker` holds: 24-hour hours and minutes. */
export interface PickerTime {
  hours: number;
  minutes: number;
}

/** A draft time (`HH:MM`, or `null` for no time) as the picker's value. */
export function toPickerTime(time: string | null): PickerTime | null {
  if (time === null) return null;
  const match = /^(\d{1,2}):(\d{2})$/.exec(time.trim());
  if (!match) return null;
  return {
    hours: Number.parseInt(match[1] as string, 10),
    minutes: Number.parseInt(match[2] as string, 10),
  };
}

/** The picker's value back into the draft's `HH:MM`; `null` (Clear) stays no time. */
export function fromPickerTime(value: PickerTime | null): string | null {
  if (value === null) return null;
  return `${String(value.hours).padStart(2, "0")}:${String(value.minutes).padStart(2, "0")}`;
}

/** The interval's steps as the "Run every" dropdown offers them. */
export const INTERVAL_OPTIONS: ReadonlyArray<{ value: IntervalMinutes; text: string }> = [
  { value: 15, text: "15 minutes" },
  { value: 30, text: "30 minutes" },
  { value: 60, text: "60 minutes" },
];

/** An hour of the day as From and Until read it: "10:00 AM"; 24 is "12:00 AM (midnight)". */
export function windowHourLabel(hour: number): string {
  const period = hour % 24 >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${hour12}:00 ${period}${hour === 24 ? " (midnight)" : ""}`;
}

/** From: 12:00 AM .. 11:00 PM. */
export const WINDOW_START_HOURS: readonly number[] = Array.from({ length: 24 }, (_, i) => i);

/** Until: 1:00 AM .. 11:00 PM, then 12:00 AM (midnight) at the end of the day. */
export const WINDOW_END_HOURS: readonly number[] = Array.from({ length: 24 }, (_, i) => i + 1);

/**
 * A draft time (24-hour `HH:MM`, the shape the draft and the cron conversion
 * keep) as the three controls show it. Anything unreadable shows as 12:00 AM
 * rather than throwing: the controls can only ever write a valid value back.
 */
export function splitTime(value: string): TimeParts {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  const hours = match ? Number.parseInt(match[1] as string, 10) : 0;
  const minutes = match ? Number.parseInt(match[2] as string, 10) : 0;
  const safeHours = hours >= 0 && hours <= 23 ? hours : 0;
  const safeMinutes = minutes >= 0 && minutes <= 59 ? minutes : 0;
  return {
    hour: String(safeHours % 12 === 0 ? 12 : safeHours % 12),
    minute: String(safeMinutes).padStart(2, "0"),
    period: safeHours < 12 ? "AM" : "PM",
  };
}

/** The three controls' values back into the draft's 24-hour `HH:MM`. */
export function joinTime(parts: TimeParts): string {
  const hour12 = Number.parseInt(parts.hour, 10) % 12;
  const hours = parts.period === "PM" ? hour12 + 12 : hour12;
  return `${String(hours).padStart(2, "0")}:${parts.minute.padStart(2, "0")}`;
}

/**
 * The resolved schedule as the save bar states it: when, in which zone, and the exact next run
 * in that zone (Session 10, B4): "Runs at 02:00 AM, Eastern (EDT). Next run Tue Sep 30,
 * 2:00 AM EDT". A disabled schedule says so instead of naming a run that will not happen, and a
 * kept custom expression says it is custom (SF2).
 */
export function scheduleSummary(
  resolved: Extract<ResolvedSchedule, { ok: true }>,
  timezone: string,
  enabled = true,
  displayZone: string = timezone,
): string {
  const zone = timezoneShortLabel(timezone, resolved.nextFireAt ?? undefined);
  const head =
    resolved.source === "custom"
      ? `Custom schedule: ${resolved.description}, ${zone}`
      : `Runs ${resolved.description.charAt(0).toLowerCase()}${resolved.description.slice(1)}, ${zone}`;
  if (!enabled) return `${head}. ${NOT_SCHEDULED_WHILE_DISABLED}`;
  if (!resolved.nextFireAt) return `${head}. ${NO_UPCOMING_RUN}`;
  // The rule keeps the schedule's zone (the customer chose it), the next run is an instant and
  // reads in the viewer's display zone, named once by its abbreviation (#1079).
  return `${head}. Next run ${formatNextFire(resolved.nextFireAt, displayZone, instantZoneName(displayZone, resolved.nextFireAt))}`;
}

/** An instant as the editor words it everywhere: in the display zone, with that zone's name (#1079). */
export function formatInstantNextFire(at: Date, displayZone: string): string {
  return formatNextFire(at, displayZone, instantZoneName(displayZone, at));
}

/**
 * The display zone's name beside an instant: the schedule picker's abbreviation where it offers
 * the zone, else the display helper's ("IST" for India, where the picker has none and the
 * plain Intl name is "GMT+5:30").
 */
export function instantZoneName(zone: string, at: Date): string {
  const picker = timezoneAbbreviation(zone, at);
  return picker.startsWith("GMT") ? zoneAbbreviation(zone, at) : picker;
}

/** "Mar 6", or "Mar 6, 2027" outside the current year in `zone`: an instant's day there. */
function shortDay(at: Date, zone: string, now: Date, weekday = false, withYear = false): string {
  const sameYear =
    !withYear && localDateOf(at, zone).slice(0, 4) === localDateOf(now, zone).slice(0, 4);
  return new Intl.DateTimeFormat("en-US", {
    timeZone: zone,
    ...(weekday ? { weekday: "short" as const } : {}),
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" as const }),
  }).format(at);
}

/** "a", "a and b", "a, b and c". */
function listInWords(items: readonly string[]): string {
  return items.length <= 1
    ? (items[0] ?? "")
    : `${items.slice(0, -1).join(", ")} and ${items.at(-1)}`;
}

/** "9:00 AM", "9:00 AM and 5:00 PM", earliest first. */
function timesInWords(times: readonly string[]): string {
  return listInWords(
    [...times].sort(byCodeUnit).map((time) => {
      const [hour, minute] = time.split(":").map(Number) as [number, number];
      return formatTime12h(hour, minute);
    }),
  );
}

/** 1 → "1st", 22 → "22nd", 13 → "13th". */
function ordinal(day: number): string {
  const tens = day % 100;
  if (tens >= 11 && tens <= 13) return `${day}th`;
  const suffix = ({ 1: "st", 2: "nd", 3: "rd" } as Record<number, string>)[day % 10] ?? "th";
  return `${day}${suffix}`;
}

/** The instant a Once row names: its local date and time in `zone` (a skipped hour moves on). */
function runDateInstant(date: string, time: string, zone: string): Date {
  const [hour, minute] = time.split(":").map(Number) as [number, number];
  const [, month, day] = date.split("-").map(Number) as [number, number, number];
  const dayStart = startOfLocalDate(date, zone);
  return nextFireAt(`${minute} ${hour} ${day} ${month} *`, zone, new Date(dayStart.getTime() - 1));
}

/** How often, in words, for the segments that run at times of day. */
function cadenceInWords(draft: ScheduleDraft, segment: ScheduleSegment): string {
  switch (segment) {
    case "Weekdays":
      return "every weekday";
    case "Weekly":
      // Monday first, as the day checkboxes read.
      return draft.weekDays.length === 0 || draft.weekDays.length === 7
        ? "every day"
        : `every ${listInWords(
            WEEK_DAYS.filter((day) => draft.weekDays.includes(day.value)).map((day) => day.label),
          )}`;
    case "Monthly":
      return draft.monthDays.length === 0
        ? "every day"
        : `monthly on the ${listInWords([...draft.monthDays].sort((a, b) => a - b).map(ordinal))}`;
    case "Yearly": {
      const { month, day } = draft.yearDate;
      return `every year on ${MONTH_NAMES[(month ?? 1) - 1]} ${day}`;
    }
    default:
      return "every day";
  }
}

/**
 * The summary line of a kept custom expression or an interval (Hourly, Custom): what it runs, then
 * the next run with its time, since an interval's times are not listed.
 */
function intervalSummaryLine(
  resolved: Extract<ResolvedSchedule, { ok: true }>,
  segment: ScheduleSegment,
  zone: string,
  now: Date,
  displayZone: string,
  enabled: boolean,
): string {
  const next = enabled ? resolved.nextFireAt : null;
  let description: string;
  if (resolved.source === "custom") description = `on a custom schedule: ${resolved.description}`;
  else if (segment === "Hourly") description = "every hour";
  else description = lowerFirst(resolved.description);
  const nextRun = next
    ? `next run ${shortDay(next, displayZone, now, true)} at ${formatTime12h(...hourMinuteIn(next, displayZone))} ${instantZoneName(displayZone, next)}`
    : noNextRunWords(enabled);
  return `Runs ${description} · ${nextRun}`;
}

/**
 * The live summary under the Frequency control (round 10, C1): "Runs every weekday at 5:00 PM
 * PDT · next run Thu, Oct 9", the next run in the schedule's zone (`zone`), past its skip dates
 * and pause. Once reads "Runs once on Mar 6 at 5:00 PM PST" (no next run). Null while the draft
 * says nothing yet, or something the bar under the card explains.
 */
export function scheduleSummaryLine(
  draft: ScheduleDraft,
  zone: string,
  now: Date = new Date(),
  displayZone: string = zone,
): string | null {
  const segment = shownSegment(draft);
  if (segment === null) return null;
  const zoned = { ...draft, timezone: zone };
  try {
    if (segment === "Once") {
      const once = onceSummaryLine(zoned, now);
      return once !== null && !draft.enabled ? `${once} · ${noNextRunWords(false)}` : once;
    }
    // Nothing to say yet, but say what is missing rather than a sentence that is not true (W2-03).
    if (segment === "Yearly" && (zoned.yearDate.month === null || zoned.yearDate.day === null)) {
      return YEARLY_HINT;
    }
    if (segment === "Monthly" && zoned.monthDays.length === 0) return MONTHLY_DAYS_HINT;
    const resolved = resolveSchedule(zoned, now);
    if (!resolved.ok) return null;
    // A disabled schedule names no next run (#1237).
    const next = draft.enabled ? resolved.nextFireAt : null;
    const abbreviation = timezoneAbbreviation(zone, resolved.nextFireAt ?? now);
    if (resolved.source === "custom" || zoned.frequency === "Interval") {
      return intervalSummaryLine(resolved, segment, zone, now, displayZone, draft.enabled);
    }
    const head = `Runs ${cadenceInWords(zoned, segment)} at ${timesInWords(chosenTimes(zoned))} ${abbreviation}`;
    // The time above is the schedule's zone. When the display zone reads another clock, the next
    // run's day carries its own time and zone name, so no day sits beside a time of another zone.
    const nextRun = next
      ? `next run ${shortDay(next, displayZone, now, true)}${
          displayZone === zone
            ? ""
            : ` at ${formatTime12h(...hourMinuteIn(next, displayZone))} ${instantZoneName(displayZone, next)}`
        }`
      : noNextRunWords(draft.enabled);
    return `${head} · ${nextRun}`;
  } catch {
    return null;
  }
}

/** The summary line's words where no next run is named: disabled, or none due. */
function noNextRunWords(enabled: boolean): string {
  return (enabled ? NO_UPCOMING_RUN : NOT_SCHEDULED_WHILE_DISABLED).toLowerCase();
}

function lowerFirst(text: string): string {
  return `${text.charAt(0).toLowerCase()}${text.slice(1)}`;
}

/** An instant's hour and minute on the wall clock in `zone`. */
function hourMinuteIn(at: Date, zone: string): [number, number] {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: zone,
    hour: "numeric",
    minute: "numeric",
    hourCycle: "h23",
  }).formatToParts(at);
  const part = (type: Intl.DateTimeFormatPartTypes): number =>
    Number(parts.find((entry) => entry.type === type)?.value ?? 0);
  return [part("hour"), part("minute")];
}

/**
 * "Runs once on Oct 10, 2026, 7:18 PM PDT", or "Runs on 3 dates, first on Oct 10, 2026, 7:18 PM
 * PDT": always the year and the zone, in the schedule's zone (W2-03).
 */
function onceSummaryLine(draft: ScheduleDraft, now: Date): string | null {
  if (runDateRowIssues(draft, now).some((issue) => issue !== null)) return null;
  const runs = draft.runDates
    .filter((row): row is { date: string; time: string } => row.date !== null && row.time !== null)
    .map((row) => runDateInstant(row.date, row.time, draft.timezone))
    .sort((a, b) => a.getTime() - b.getTime());
  const first = runs[0];
  if (!first) return null;
  const when = `${shortDay(first, draft.timezone, now, false, true)}, ${formatTime12h(
    ...hourMinuteIn(first, draft.timezone),
  )} ${timezoneAbbreviation(draft.timezone, first)}`;
  return runs.length === 1
    ? `Runs once on ${when}`
    : `Runs on ${runs.length} dates, first on ${when}`;
}

/** The frequency a read expression has: Monthly with month days, Weekly with weekdays, else Daily. */
function builderFrequencyOf(monthDays: number[], weekDays: number[]): ScheduleFrequency {
  if (monthDays.length > 0) return "Monthly";
  return weekDays.length > 0 ? "Weekly" : "Daily";
}

/** Weekly on exactly Monday to Friday reads as the Weekdays segment (round 10, C1). */
function segmentOfTimes(
  frequency: ScheduleFrequency,
  weekDays: readonly number[],
): ScheduleSegment {
  if (frequency === "Weekly") {
    return weekDays.join(",") === WEEKDAY_VALUES.join(",") ? "Weekdays" : "Weekly";
  }
  return frequency === "Monthly" ? "Monthly" : "Daily";
}

/** The draft's 24-hour `HH:MM` rows for one minute past each hour. */
function timesOf(hours: readonly number[], minute: number): string[] {
  return hours.map((hour) => `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`);
}

/**
 * A Yearly expression (`m h D M *`, round 10, C1) read back as the Yearly builder, trusted only
 * when it round-trips. `undefined` when the expression is not one date of the year (the other
 * readers try it), `null` when it is one the builder would not write as it is stored.
 */
function yearlyDraftFromCron(
  parts: readonly string[],
): ReturnType<typeof draftFromCron> | undefined {
  const yearDate = yearDateFromCron(parts.join(" "));
  if (!yearDate) return undefined;
  const [minute, hours] = parts as [string, string];
  const minuteValue = Number.parseInt(minute, 10);
  const hourValues = hours.split(",").map((hour) => Number.parseInt(hour, 10));
  if (!Number.isFinite(minuteValue) || hourValues.some((hour) => !(hour >= 0 && hour <= 23))) {
    return null;
  }
  const parsed = {
    frequency: "Yearly" as const,
    segment: "Yearly" as const,
    times: timesOf(hourValues, minuteValue),
    weekDays: [],
    monthDays: [],
    yearDate,
  };
  try {
    const roundTrip = friendlyToCronExpression(toBuilderFriendly({ ...emptyDraft(), ...parsed }));
    return roundTrip === parts.join(" ") ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * The stored expression turned back into the builder's own fields, or `null` when the builder
 * cannot represent it EXACTLY (SF2).
 *
 * The parse below is deliberately loose (it reads the first number of a range, ignores the
 * month, and reads a named day as no day at all), so its result is only trusted when it
 * round-trips: the draft is collapsed back through `friendlyToCronExpression`, the same function
 * Save uses, and must give the stored expression again, whitespace aside. Anything else
 * (`0 2 * * 1-5`, `0 2 * * MON`, `30 9-17 * * *`, the walkthrough's `0 3 29 2 *`) answers
 * `null`, so the editor keeps it as a custom schedule and a rename cannot re-time it.
 *
 * An expression the interval builder writes (`*\/30 10-17 * * 1,2,3,4,5`, `0 9-16 * * *`) reads
 * back as the Interval frequency, through the shared `intervalFromCron`, which has the same
 * round-trip rule; only then do the interval fields appear in the answer.
 */
export function draftFromCron(
  cron: string | null,
):
  | (Pick<ScheduleDraft, "frequency" | "segment" | "times" | "weekDays" | "monthDays"> &
      Partial<Pick<ScheduleDraft, "intervalMinutes" | "windowStart" | "windowEnd" | "yearDate">>)
  | null {
  if (!cron) return null;
  const interval = intervalFromCron(cron);
  if (interval) {
    const hourOf = (value: string, edge: "start" | "end"): number => {
      const match = /^(\d{2}):00(AM|PM)$/.exec(value) as RegExpExecArray;
      const hour12 = Number.parseInt(match[1] as string, 10) % 12;
      const hour = match[2] === "PM" ? hour12 + 12 : hour12;
      return edge === "end" && hour === 0 ? 24 : hour;
    };
    const windowStart = hourOf(interval.windowStart, "start");
    const windowEnd = hourOf(interval.windowEnd, "end");
    // Round 10, C1: every hour of every day is the Hourly segment; any other interval is Custom.
    const hourly =
      interval.intervalMinutes === 60 &&
      windowStart === 0 &&
      windowEnd === 24 &&
      interval.weekDays.length === 0;
    return {
      frequency: "Interval",
      segment: hourly ? "Hourly" : "Custom",
      times: [],
      weekDays: interval.weekDays,
      monthDays: [],
      intervalMinutes: interval.intervalMinutes,
      windowStart,
      windowEnd,
    };
  }
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return null;
  const yearly = yearlyDraftFromCron(parts);
  if (yearly !== undefined) return yearly;
  const [minute, hours, dayOfMonth, , dayOfWeek] = parts as [
    string,
    string,
    string,
    string,
    string,
  ];
  const minuteValue = Number.parseInt(minute, 10);
  if (!Number.isFinite(minuteValue)) return null;

  const hourValues = hours
    .split(",")
    .map((hour) => Number.parseInt(hour, 10))
    .filter((hour) => Number.isFinite(hour) && hour >= 0 && hour <= 23);
  if (hourValues.length === 0) return null;

  const weekDays =
    dayOfWeek === "*"
      ? []
      : dayOfWeek
          .split(",")
          .map((day) => Number.parseInt(day, 10))
          .filter((day) => Number.isFinite(day) && day >= 0 && day <= 6);
  const monthDays =
    dayOfMonth === "*"
      ? []
      : dayOfMonth
          .split(",")
          .map((day) => Number.parseInt(day, 10))
          .filter((day) => Number.isFinite(day) && day >= 1 && day <= 31);

  // A day-of-month list wins over a day-of-week list, matching
  // `friendlyToCron`, which only ever writes one of the two.
  const frequency = builderFrequencyOf(monthDays, weekDays);
  const parsed: Pick<ScheduleDraft, "frequency" | "segment" | "times" | "weekDays" | "monthDays"> =
    {
      frequency,
      segment: segmentOfTimes(frequency, weekDays),
      times: timesOf(hourValues, minuteValue),
      weekDays,
      monthDays,
    };

  // Trusted only when it round-trips (SF2): a lossy read would otherwise be saved back on the
  // next rename and silently re-time the schedule.
  let roundTrip: string;
  try {
    roundTrip = friendlyToCronExpression(toBuilderFriendly({ ...emptyDraft(), ...parsed }));
  } catch {
    return null;
  }
  return roundTrip === parts.join(" ") ? parsed : null;
}

/**
 * A skip date as a chip reads it: "Fri, Dec 25, 2026". The value is a calendar day with no zone,
 * so it is formatted as UTC midnight in UTC, which can never move it to the day before.
 */
export function formatSkipDate(value: string): string {
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: "UTC",
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(new Date(`${value}T00:00:00Z`));
  } catch {
    return value;
  }
}

/** A `Date` from a date picker (a local calendar day in the browser) as `YYYY-MM-DD`. */
export function pickerDateToDay(date: Date): string {
  return [
    String(date.getFullYear()).padStart(4, "0"),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");
}

/**
 * Today as `YYYY-MM-DD` in the SCHEDULE's zone, not the browser's (P9 review r1): the server
 * prunes skip dates and bounds a pause against the schedule's own today, so a field that judged
 * "past" by the browser's calendar would refuse a day the schedule can still skip (browser ahead
 * of the zone) or offer one the server drops or refuses (browser behind it). A zone the browser
 * cannot resolve (an empty draft) falls back to the browser's day.
 */
export function todayInZone(timezone: string, now: Date = new Date()): string {
  try {
    return localDateOf(now, timezone);
  } catch {
    return pickerDateToDay(now);
  }
}

/** True while a schedule's pause is still ahead of `now`. */
export function isPaused(pausedUntil: string | null | undefined, now: Date = new Date()): boolean {
  return Boolean(pausedUntil) && new Date(pausedUntil as string).getTime() > now.getTime();
}

/**
 * When a pause ends, in one wording for the Pause dialog's notice, the list's badge, the State
 * hover and the editor's bar (#1078 review): in the reader's display zone, always named, "Sep 30,
 * 12:00 AM PDT" when it ends at the start of a day there (which is what the Pause dialog writes),
 * else the exact instant, "Tue Sep 29, 9:00 PM PDT". The pause end is an instant the reader chose
 * in their own zone, so every screen names the same day.
 */
export function formatPauseEnd(pausedUntil: string, displayZone: string): string {
  const end = new Date(pausedUntil);
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: displayZone,
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hourCycle: "h23",
    }).formatToParts(end);
    const part = (type: Intl.DateTimeFormatPartTypes): string =>
      parts.find((entry) => entry.type === type)?.value ?? "";
    const atMidnight = Number(part("hour")) === 0 && Number(part("minute")) === 0;
    return atMidnight
      ? `${part("month")} ${part("day")}, 12:00 AM ${instantZoneName(displayZone, end)}`
      : formatInstantNextFire(end, displayZone);
  } catch {
    return formatInstantNextFire(end, FALLBACK_TIMEZONE);
  }
}
